import { Injectable } from '@angular/core';
import { DatasetInterface } from 'app/models/dataset.model';
import { PlaylistInterface } from 'app/models/editorial.model';
import { Resource } from 'app/models/resource.model';
import { SystemInterface } from 'app/models/system.model';
import { DateRangeInterface } from 'app/modules/common/datetime/dateslots/dateslot.interface';
import {
  TimeslotInterface,
  TimeslotsCalendarInterface,
  WeeklySchedulingInterface,
} from 'app/modules/common/timeslot/timeslot.interface';
import { PlaylistItemInterface, PlaylistItemNestedInterface } from 'app/modules/editorial/editorial.interface';
import { DataTransformAbstractPost } from 'app/modules/global/classes/data.transform.abstract.down';
import { FluidDatePipe } from 'app/modules/global/pipes/fluidDate.pipe';

@Injectable({
  providedIn: 'root',
})
export class SystemDataTransformPost extends DataTransformAbstractPost {
  constructor(protected _fluidDatePipe: FluidDatePipe) {
    super();
  }

  transform(system: SystemInterface): SystemInterface {
    console.log('>>> SYSTEM TRANSFORM DOWN', system);
    // if ((<any>system)._statusTimeslot) {
    //   delete (<any>system)._statusTimeslot;
    // }
    if (system.on) {
      system.on = this.normalizeTime(system.on.toString());
    }
    if (system.off) {
      system.off = this.normalizeTime(system.off.toString());
    }
    if (system.startAt) {
      system.startAt = this.normalizeDate(system.startAt.toString());
    }
    if (system.endAt) {
      system.endAt = this.normalizeDate(system.endAt.toString());
    }

    system.editorials = [];
    this.normalizePlaylists(system)
      .normalizeScreens(system)
      .normalizeStandAlone(system)
      .normalizeMonitor(system)
      .normalizeCalendarScheduling(system)
      .normalizeWeeklyScheduling(system)
      .normalizeBrightnessScheduling(system);
    return system;
  }

  private normalizeCalendarScheduling(system: SystemInterface): this {
    system.calendarScheduling = this.calendarSchedulings(system.calendarScheduling);
    return this;
  }

  protected calendarSchedulings(calendarScheduling: TimeslotsCalendarInterface[]): TimeslotsCalendarInterface[] {
    if (!Array.isArray(calendarScheduling)) {
      return calendarScheduling;
    }
    calendarScheduling.forEach((calendar) => {
      if (calendar.startAt) {
        calendar.startAt = this.normalizeDate(calendar.startAt.toString());
      }
      if (calendar.endAt) {
        calendar.endAt = this.normalizeDate(calendar.endAt.toString());
      }
      calendar.weeklySchedulings = this.normalizeWeeklySchedulings(calendar.weeklySchedulings);
      calendar.timeslots = this.normalizeTimeslots(calendar.timeslots);
    });
    return calendarScheduling;
  }

  protected normalizeWeeklySchedulings(weeklyScheduling: WeeklySchedulingInterface[]): WeeklySchedulingInterface[] {
    if (!Array.isArray(weeklyScheduling)) {
      return weeklyScheduling;
    }
    weeklyScheduling.forEach((day) => {
      day.timeslots = this.normalizeTimeslots(day.timeslots);
    });
    return weeklyScheduling;
  }

  protected normalizeTimeslots(timeslots: TimeslotInterface[]): TimeslotInterface[] {
    timeslots = timeslots || [];
    timeslots.forEach((timeslot: TimeslotInterface) => {
      this.normalizeTimeslot(timeslot);
    });
    return timeslots;
  }

  private normalizePlaylists(system: SystemInterface): this {
    if (!Array.isArray(system.playlists)) {
      return this;
    }
    system.playlists.forEach((playlist: PlaylistInterface) => {
      this.normalizePlaylist(playlist);
    });
    return this;
  }

  private normalizeStandAlone(system: SystemInterface) {
    if (system.standAlone) {
      system.standAlone.resources = system.standAlone.resources || [];
      system.standAlone.resources.forEach((resourceInt: PlaylistItemInterface) => {
        if (resourceInt.resource) {
          resourceInt.resource = <Resource>{ id: resourceInt.resource.id };
        }
        if (resourceInt.dcontent) {
          resourceInt.dcontent = <DatasetInterface>{ id: resourceInt.dcontent.id };
        }
      });
    }
    return this;
  }

  normalizePlaylist(playlist: PlaylistInterface) {
    // REMOVE SPECIAL FIELDS BASED ON PERMISSIONS
    delete (<any>playlist).disabled;
    delete (<any>playlist).hidden;

    // NORMALIZE PLAYLIST DATES
    playlist.timeslots = this.normalizeTimeslots(playlist.timeslots);
    playlist.weeklyScheduling = this.normalizeWeeklySchedulings(playlist.weeklyScheduling);
    playlist.calendarScheduling = this.calendarSchedulings(playlist.calendarScheduling);

    playlist.resources = playlist.resources || [];
    playlist.resources.forEach((resourceInt: PlaylistItemInterface) => {
      delete resourceInt.context;
      resourceInt.timeslots = this.normalizeTimeslots(resourceInt.timeslots);
      resourceInt.weeklyScheduling = this.normalizeWeeklySchedulings(resourceInt.weeklyScheduling);
      resourceInt.calendarScheduling = this.calendarSchedulings(resourceInt.calendarScheduling);

      if (resourceInt.resource) {
        resourceInt.resource = <Resource>{ id: resourceInt.resource.id };
      }
      if (resourceInt.dcontent) {
        resourceInt.dcontent = <DatasetInterface>{ id: resourceInt.dcontent.id };
      }
      this.normalizeNested(resourceInt.nested);
    });
  }

  normalizeNested(nested: PlaylistItemNestedInterface) {
    if (nested) {
      if (!Array.isArray(nested.resources)) {
        return this;
      }
      nested.resources.forEach((resourceInt: PlaylistItemInterface) => {
        delete resourceInt.context;
        // THIS SHOULDN'T EXIST IN NESTED RESOURCES
        this.normalizeTimeslots(resourceInt.timeslots);
        if (resourceInt.resource) {
          resourceInt.resource = <Resource>{ id: resourceInt.resource.id };
        }
        if (resourceInt.dcontent) {
          resourceInt.dcontent = <DatasetInterface>{ id: resourceInt.dcontent.id };
        }
      });
    }
  }

  normalizeRange(range: DateRangeInterface[]) {
    if (!Array.isArray(range)) {
      return this;
    }
    range.forEach((rangeItem: DateRangeInterface) => {
      if (rangeItem.startAt) {
        rangeItem.startAt = this.normalizeDate(rangeItem.startAt.toString());
      }
      if (rangeItem.endAt) {
        rangeItem.endAt = this.normalizeDate(rangeItem.endAt.toString());
      }
    });
  }

  private normalizeWeeklyScheduling(system: SystemInterface): this {
    system.weeklyScheduling = this.normalizeWeeklySchedulings(system.weeklyScheduling);
    return this;
  }

  private normalizeScreens(system: SystemInterface): this {
    if (!system.screens) {
      return this;
    }
    system.screens.forEach((screen) => {
      if (!screen.mirror_guid) {
        delete screen.mirror_guid;
      }
    });
    return this;
  }

  private normalizeMonitor(system: SystemInterface): this {
    if (!system.monitor) {
      return this;
    }
    if (system.monitor.range) {
      this.normalizeMonitorRange(system.monitor.range);
    }
    system.monitor.weeklyScheduling = this.normalizeWeeklySchedulings(system.monitor.weeklyScheduling);

    return this;
  }

  private normalizeBrightnessScheduling(system: SystemInterface): this {
    if (!Array.isArray(system.brightnessScheduling)) {
      return this;
    }
    system.brightnessScheduling.forEach((item) => {
      if (item.range) {
        this.normalizeTimeslot(item.range);
      }
    });
    return this;
  }

  protected normalizeTimeslot(timeslot: TimeslotInterface): TimeslotInterface {
    if (timeslot.from) {
      timeslot.from = this.normalizeTime(timeslot.from.toString());
    }
    if (timeslot.to) {
      timeslot.to = this.normalizeTime(timeslot.to.toString());
    }
    return timeslot;
  }

  protected normalizeMonitorRange(range: TimeslotInterface): TimeslotInterface {
    if (range.startAt) {
      range.startAt = this.normalizeTime(range.startAt.toString());
    }
    if (range.endAt) {
      range.endAt = this.normalizeTime(range.endAt.toString());
    }
    return range;
  }
}
